/**
 * Panel „Insights" der Uebersicht - woertliche Portierung von `index.html:14744-14774`
 * (samt des Helfers `insight`, `index.html:14664`).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Die vier Karten haengen an VIER unterschiedlichen Bedingungen, und keine davon ist der
 *    jeweils andere Zweig derselben Frage:
 *    - Karte 1 (Zahlungen): Ton und Symbol nach `overduePayments`/`dueSoonPayments`; die
 *      UEBERSCHRIFT nennt bei ueberfaelligen deren Zahl, sonst die der bald faelligen - auch wenn
 *      diese 0 ist („0 Zahlungen stehen an").
 *    - Karte 2 (ETA): Ton/Symbol/Ueberschrift nach `missingEta`, der TEXT dagegen nach
 *      `arrivingSoon`. Beide Groessen sind unabhaengig; „Alle ETA-Daten gepflegt" kann also
 *      zusammen mit „Keine kurzfristige Anlieferung angekuendigt." stehen.
 *    - Karte 3 (Dokumente): der Ton kommt aus `documentPct`, der Text aus den absoluten Zahlen.
 *      `documentPct` selbst steht NIRGENDS im Text.
 *    - Karte 4 (Stammdaten): Ton/Symbol/Ueberschrift nach `masterIssues`, der Text nennt den
 *      groessten Lieferanten - fachlich ohne Bezug zur Ueberschrift.
 * 2. Karte 3 hat als einzige ein festes Symbol (`files`) unabhaengig vom Ton.
 * 3. Die Schwellen von Karte 3 sind `< 65` -> `warning`, `>= 90` -> `good`, dazwischen `info`.
 *    Bei leerem Bestand ist `documentPct` 100 (`total === 0` -> 100), also `good`.
 */
import { Ikone } from "../Ikone.tsx";
import { NUM } from "../../lib/format.ts";

export interface EinblickeProps {
  overduePayments: number;
  dueSoonPayments: number;
  missingEta: number;
  arrivingSoon: number;
  documentDone: number;
  documentTotal: number;
  documentPct: number;
  masterIssues: number;
  /** `topSupplier` aus `index.html:14639` - `[name, anzahl]` oder `undefined`. */
  topLieferant: readonly [string, number] | undefined;
}

/** index.html:14664 - `insight(tone, iconName, title, text)`. */
function Karte({
  ton,
  symbol,
  titel,
  text,
}: {
  ton: string;
  symbol: string;
  titel: string;
  text: string;
}) {
  return (
    <div className={`ops-insight ${ton}`}>
      <span className="ops-insight-mark">
        <Ikone name={symbol} />
      </span>
      <div>
        <strong>{titel}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

export function Einblicke({
  overduePayments,
  dueSoonPayments,
  missingEta,
  arrivingSoon,
  documentDone,
  documentTotal,
  documentPct,
  masterIssues,
  topLieferant,
}: EinblickeProps) {
  return (
    <aside className="ops-panel ops-insights-panel">
      <div className="ops-panel-head">
        <div className="ops-panel-title">
          <h2>Insights</h2>
          <p>Automatisch aus den aktuellen Bestelldaten</p>
        </div>
      </div>
      <div className="ops-insights">
        <Karte
          ton={overduePayments ? "urgent" : dueSoonPayments ? "warning" : "good"}
          symbol={overduePayments ? "triangle-alert" : dueSoonPayments ? "clock-3" : "circle-check-big"}
          titel={
            overduePayments
              ? `${NUM.format(overduePayments)} Zahlungen überfällig`
              : `${NUM.format(dueSoonPayments)} Zahlungen stehen an`
          }
          text={
            overduePayments
              ? "Finance sollte diese Vorgänge zuerst klären."
              : dueSoonPayments
                ? "Fälligkeit innerhalb der nächsten sieben Tage."
                : "Keine Zahlung innerhalb der nächsten sieben Tage."
          }
        />
        <Karte
          ton={missingEta ? "warning" : "good"}
          symbol={missingEta ? "calendar-clock" : "circle-check-big"}
          titel={
            missingEta ? `${NUM.format(missingEta)} Bestellungen ohne ETA` : "Alle ETA-Daten gepflegt"
          }
          text={
            arrivingSoon
              ? `${NUM.format(arrivingSoon)} Anlieferungen werden in den nächsten 14 Tagen erwartet.`
              : "Keine kurzfristige Anlieferung angekündigt."
          }
        />
        <Karte
          ton={documentPct < 65 ? "warning" : documentPct >= 90 ? "good" : "info"}
          symbol="files"
          titel="Dokumentenabdeckung"
          text={`${NUM.format(documentDone)} von ${NUM.format(documentTotal)} erforderlichen Dokumentstatus sind erledigt.`}
        />
        <Karte
          ton={masterIssues ? "warning" : "good"}
          symbol={masterIssues ? "database" : "circle-check-big"}
          titel={
            masterIssues
              ? `${NUM.format(masterIssues)} Stammdatenthemen offen`
              : "Stammdaten vollständig"
          }
          text={
            topLieferant
              ? `${topLieferant[0]} ist mit ${NUM.format(topLieferant[1])} aktiven Bestellungen am stärksten vertreten.`
              : "Noch keine aktiven Lieferanten vorhanden."
          }
        />
      </div>
    </aside>
  );
}
